// ============================================================================
// POSTING SERVICE — Personal Finance V2 (Blueprint §6.1)
// Pair-aware CRUD. Garante INV-1 a INV-10 em tempo de escrita.
// Único ponto de entrada para criação/edição de lançamentos PF.
// ============================================================================

import {
  batchCreatePostings,
  batchUpdatePostings,
  queryByPairId,
} from "../ports/firestoreRepo.js";
import {
  validateDraft,
  validatePairConsistency,
} from "../domain/invariants.js";
import {
  canTransition,
  validateSettlement,
  STATUS,
  TRANSITION,
} from "../domain/statusMachine.js";
import { logPairTransition, logTransition } from "./auditService.js";

// ============================================================================
// HELPERS
// ============================================================================

/** Sanitiza valor para 2 casas decimais */
function sanitizeMoney(val) {
  return Math.round(Number(val) * 100) / 100;
}

/** Propaga campos extras do draft para o documento (SSOT — Firestore armazena tudo) */
function spreadExtraFields(draft) {
  return {
    juros: draft.juros ?? null,
    desconto: draft.desconto ?? null,
    acrescimo: draft.acrescimo ?? null,
    tarifa: draft.tarifa ?? null,
    natureza: draft.natureza || null,
    templateId: draft.templateId || null,
    descOut: draft.descOut || null,
    numeroDocumento: draft.numeroDocumento || null,
    dataEmissao: draft.dataEmissao || null,
    paidBy: draft.paidBy || null,
    reimbursable: draft.reimbursable || false,
    needsSettlement: draft.needsSettlement || false,
    modoLancamento: draft.modoLancamento || null,
    sudNivel1: draft.sudNivel1 || null,
    sudNivel2: draft.sudNivel2 || null,
    sudNivel3: draft.sudNivel3 || null,
    sudVars: draft.sudVars || null,
    valorPrevisto: draft.valorPrevisto ?? null,
  };
}

/** Detecta se a despesa precisa de par (Rota B/C) */
export function needsPair(expense) {
  if (expense.group === "RECEITAS") return false;
  const ctx = [expense.account, expense.description, expense.recorrencia || ""]
    .join(" ")
    .toLowerCase();
  return (
    ctx.includes("pj") ||
    ctx.includes("c6") ||
    ctx.includes("eliane") ||
    ctx.includes("retirada")
  );
}

/** Gera a RECEITA counterpart a partir da despesa */
function buildIncomeCounterpart(expense, pairId) {
  const prefix =
    expense.vinculo === "eliane"
      ? "Retirada Sócio -> Reembolso Eliane"
      : "Retirada Sócio";
  return {
    description: `${prefix} -> ${expense.description}`,
    value: expense.value,
    date: expense.date,
    competenceDate: expense.competenceDate || expense.date,
    settlementDate: expense.settlementDate || null,
    recorrencia: expense.recorrencia || "Única",
    parcela: expense.parcela || null,
    group: "RECEITAS",
    category: "Retirada de Sócio",
    account: expense.account || "C6 Bank PJ",
    vinculo: expense.vinculo || null,
    status: expense.status || STATUS.PENDING,
    pairId,
    sortOrder: 0,
    pairedSortKey: pairId,
    historyCount: 0,
    lastTransition: TRANSITION.CREATE,
  };
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Cria um PAR (DESPESA + RECEITA "Retirada de Sócio") atomicamente.
 * Se needsPair() retorna false, cria apenas a despesa (createSingle).
 *
 * @param {object} draft — campos do formulário
 * @param {object} [options]
 * @param {boolean} [options.forcePair=false] — forçar criação do par mesmo se auto-detect falhar
 * @returns {Promise<{ expenseId: string, incomeId?: string, pairId?: string }>}
 */
export async function createPair(draft, { forcePair = false } = {}) {
  const value = sanitizeMoney(draft.value || draft.amount);
  const status = draft.status || STATUS.PENDING;

  // Monta o doc de despesa com campos extras propagados
  const expense = {
    description: draft.description,
    value,
    date: draft.date,
    competenceDate: draft.competenceDate || draft.date,
    settlementDate: draft.settlementDate || null,
    recorrencia: draft.recorrencia || "Única",
    parcela: draft.parcela || null,
    group: draft.group || draft.type,
    category: draft.category || draft.subCategory,
    account: draft.account || "",
    vinculo: detectVinculo(draft),
    status,
    historyCount: 0,
    lastTransition: TRANSITION.CREATE,
    ...spreadExtraFields(draft),
  };

  // Valida
  const check = validateDraft(expense);
  if (!check.ok) throw new Error(`Validação falhou: ${check.error}`);

  // Status machine
  const trans = canTransition(null, status);
  if (!trans.valid) throw new Error(trans.error);

  const shouldPair = forcePair || needsPair(expense);

  if (!shouldPair) {
    // Rota A — single entry
    const [expenseId] = await batchCreatePostings([{ data: expense }]);
    await logTransition({
      postingId: expenseId,
      action: TRANSITION.CREATE,
      after: expense,
    });
    return { expenseId };
  }

  // Rota B/C — par atômico
  const docs = [];
  const expenseDoc = { data: { ...expense } };
  docs.push(expenseDoc);

  // Cria os docs — primeiro sem pairId, depois atualiza
  const [expenseId] = await batchCreatePostings([{ data: expense }]);

  // Agora cria a RECEITA com pairId = expenseId
  const income = buildIncomeCounterpart(
    { ...expense, vinculo: expense.vinculo },
    expenseId,
  );

  // Valida par
  const pairCheck = validatePairConsistency(income, {
    ...expense,
    pairId: expenseId,
  });
  if (!pairCheck.ok) throw new Error(`Par inválido: ${pairCheck.error}`);

  const [incomeId] = await batchCreatePostings([{ data: income }]);

  // Atualiza a despesa com pairId + sortOrder
  await batchUpdatePostings([
    {
      id: expenseId,
      patch: { pairId: expenseId, sortOrder: 1, pairedSortKey: expenseId },
    },
  ]);

  // Audit log
  await logPairTransition({
    pairId: expenseId,
    expenseId,
    incomeId,
    action: TRANSITION.CREATE,
    after: { expense, income },
  });

  return { expenseId, incomeId, pairId: expenseId };
}

/**
 * Cria uma entrada avulsa (sem par). Uso: RECEITAS manuais, Rota A.
 */
export async function createSingle(draft) {
  const value = sanitizeMoney(draft.value || draft.amount);
  const status = draft.status || STATUS.PENDING;

  const posting = {
    description: draft.description,
    value,
    date: draft.date,
    competenceDate: draft.competenceDate || draft.date,
    settlementDate: draft.settlementDate || null,
    recorrencia: draft.recorrencia || "Única",
    parcela: draft.parcela || null,
    group: draft.group || draft.type,
    category: draft.category || draft.subCategory,
    account: draft.account || "",
    vinculo: detectVinculo(draft),
    status,
    historyCount: 0,
    lastTransition: TRANSITION.CREATE,
    ...spreadExtraFields(draft),
  };

  const check = validateDraft(posting);
  if (!check.ok) throw new Error(`Validação falhou: ${check.error}`);

  const [id] = await batchCreatePostings([{ data: posting }]);
  await logTransition({
    postingId: id,
    action: TRANSITION.CREATE,
    after: posting,
  });
  return { id };
}

/**
 * Liquida um par: PENDING → PAID em ambas as pontas.
 * @param {string} pairId
 * @param {{ settlementDate: string, bling_ids?: object }} settlement
 */
export async function settlePair(pairId, { settlementDate, bling_ids = null }) {
  const sCheck = validateSettlement(STATUS.PAID, settlementDate);
  if (!sCheck.ok) throw new Error(sCheck.error);

  const docs = await queryByPairId(pairId);
  if (docs.length === 0) throw new Error(`Par '${pairId}' não encontrado.`);

  const updates = [];
  for (const d of docs) {
    const trans = canTransition(d.status, STATUS.PAID);
    if (!trans.valid) throw new Error(`${d.id}: ${trans.error}`);
    updates.push({
      id: d.id,
      patch: {
        status: STATUS.PAID,
        settlementDate,
        ...(bling_ids ? { bling_ids } : {}),
        historyCount: (d.historyCount || 0) + 1,
        lastTransition: TRANSITION.SETTLE,
      },
    });
  }

  await batchUpdatePostings(updates);

  const expense = docs.find((d) => d.group !== "RECEITAS");
  const income = docs.find((d) => d.group === "RECEITAS");
  if (expense && income) {
    await logPairTransition({
      pairId,
      expenseId: expense.id,
      incomeId: income.id,
      action: TRANSITION.SETTLE,
      before: { status: expense.status },
      after: { status: STATUS.PAID, settlementDate },
    });
  }
}

/**
 * Cancela um par (soft-delete): qualquer status → CANCELLED.
 */
export async function cancelPair(pairId, reason = "") {
  const docs = await queryByPairId(pairId);
  if (docs.length === 0) throw new Error(`Par '${pairId}' não encontrado.`);

  const updates = docs.map((d) => ({
    id: d.id,
    patch: {
      status: STATUS.CANCELLED,
      cancellationReason: reason,
      cancelledAt: new Date().toISOString(),
      historyCount: (d.historyCount || 0) + 1,
      lastTransition: TRANSITION.CANCEL,
    },
  }));

  await batchUpdatePostings(updates);

  const expense = docs.find((d) => d.group !== "RECEITAS");
  const income = docs.find((d) => d.group === "RECEITAS");
  if (expense && income) {
    await logPairTransition({
      pairId,
      expenseId: expense.id,
      incomeId: income.id,
      action: TRANSITION.CANCEL,
      before: { status: expense.status },
      after: { status: STATUS.CANCELLED },
      reason,
    });
  }
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

function detectVinculo(draft) {
  // Se o draft já tem vinculo explícito, respeita (SSOT)
  if (draft.vinculo) return draft.vinculo;
  // Fallback: detecta 'eliane' no contexto
  const ctx = [draft.account, draft.recorrencia, draft.description]
    .join(" ")
    .toLowerCase();
  if (ctx.includes("eliane")) return "eliane";
  return null;
}
